/**
 * Canonical telemetry selectors (spec `vehicle-realtime-data-consistency`,
 * design "Hook Interface" / step 5 part 2).
 *
 * Every selector is a pure `(state, plateNumber)` function over the reducer state
 * of `./reducer`, so the same reconciliation can be read from the hook, from a
 * container, or from a test without rendering anything. `useVehicleTelemetry`
 * binds them to its own state for consumers.
 *
 * Lookups are O(1): the reducer keeps `currentByPlate` / `tableByPlate` keyed by
 * plate number and already stores table rows newest-first.
 *
 * Speed is returned as raw metres per second. The m/s -> km/h conversion and the
 * "NA" fallback stay presentation concerns (design 3.3).
 */

/** Shared empty result so an unknown plate keeps a stable array reference. */
const EMPTY_ROWS = [];

const isValidPlate = (plateNumber) =>
  plateNumber !== null && plateNumber !== undefined && plateNumber !== "";

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const readEntry = (state, plateNumber) => {
  if (!state || !isValidPlate(plateNumber)) return null;
  return state.currentByPlate?.[plateNumber] ?? null;
};

/**
 * Full canonical entry of a plate: `{ plateNumber, sourceId, latestPacketAt,
 * latestPacketType, movement, ignition, position }`.
 *
 * @returns {Object|null} The entry, or `null` when the plate is unknown.
 */
export const getCurrentTelemetryByPlateNumber = (state, plateNumber) =>
  readEntry(state, plateNumber);

/**
 * Canonical movement observation of a plate: the newest speed observation across
 * the latest-vehicles aggregate, the route response and SignalR.
 *
 * @returns {Object|null} `{ speed, direction, observedAt, packetId, source }` or `null`.
 */
export const getCurrentMovementByPlateNumber = (state, plateNumber) =>
  readEntry(state, plateNumber)?.movement ?? null;

/**
 * Raw canonical speed in metres per second. `0` is a valid speed and is returned
 * as `0`; only a missing observation yields `null`.
 *
 * @returns {number|null}
 */
export const getCurrentSpeed = (state, plateNumber) =>
  getCurrentMovementByPlateNumber(state, plateNumber)?.speed ?? null;

/** Observation time of the canonical movement, or `null` when freshness is unknown. */
export const getCurrentMovementObservedAt = (state, plateNumber) =>
  toFiniteNumber(getCurrentMovementByPlateNumber(state, plateNumber)?.observedAt);

const isNewestFirst = (rows) => {
  for (let index = 1; index < rows.length; index += 1) {
    const previous = toFiniteNumber(rows[index - 1]?.sourceTime);
    const current = toFiniteNumber(rows[index]?.sourceTime);
    // Rows with an unknown time sort last, matching the reducer's ordering.
    if (previous === null && current !== null) return false;
    if (previous !== null && current !== null && current > previous) return false;
  }
  return true;
};

/**
 * Real packets of a plate, sorted by `sourceTime` descending.
 *
 * Only normalized packets that actually arrived are returned - an aggregate row
 * built from the latest-vehicles fields would mix observations made at different
 * times, so it is never fabricated here (design 3.5). The array reference is
 * preserved when the reducer's stored order already satisfies the contract, so
 * consumers do not re-render on every read.
 *
 * @returns {Array<Object>} Newest-first rows, empty when the plate is unknown.
 */
export const getTableDataByPlateNumber = (state, plateNumber) => {
  if (!state || !isValidPlate(plateNumber)) return EMPTY_ROWS;

  const rows = state.tableByPlate?.[plateNumber];
  if (!Array.isArray(rows) || rows.length === 0) return EMPTY_ROWS;
  if (isNewestFirst(rows)) return rows;

  return [...rows].sort((a, b) => {
    const aTime = toFiniteNumber(a?.sourceTime);
    const bTime = toFiniteNumber(b?.sourceTime);
    if (aTime === bTime) return 0;
    if (aTime === null) return 1;
    if (bTime === null) return -1;
    return bTime - aTime;
  });
};

/**
 * Newest speed-bearing row of the live table (revised consistency contract).
 * Consumers compare this row against the canonical movement instead of assuming
 * the first row carries speed.
 *
 * @returns {Object|null}
 */
export const getNewestSpeedRowByPlateNumber = (state, plateNumber) =>
  getTableDataByPlateNumber(state, plateNumber).find((row) => row?.movementObservedAt != null) ??
  null;
